import { createHandleFromEmail, normalizeUsername } from "../utils/userIdentity";

export const USER_SCHEMA_VERSION = 1;

function isNonEmptyString(value) {
  return typeof value === "string" && value.trim().length > 0;
}

function toSafeString(value, fallback = "") {
  return isNonEmptyString(value) ? String(value).trim() : fallback;
}

function toSafeBoolean(value, fallback = false) {
  return typeof value === "boolean" ? value : fallback;
}

function toSafeNonNegativeInt(value, fallback = 0) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  const integer = Math.trunc(parsed);
  return integer >= 0 ? integer : fallback;
}

function resolveUsername({ existing = {}, input = {}, firebaseUser }) {
  const candidate =
    normalizeUsername(input.username || "") ||
    normalizeUsername(existing.username || "") ||
    normalizeUsername(input.displayName || "") ||
    normalizeUsername(existing.displayName || "") ||
    createHandleFromEmail(firebaseUser?.email || existing.email || "", firebaseUser?.uid || existing.uid || "");

  return candidate || createHandleFromEmail("", firebaseUser?.uid || existing.uid || "");
}

function resolveName({ existing = {}, input = {}, fallback = "" }) {
  return (
    toSafeString(input.name, "") ||
    toSafeString(input.displayName, "") ||
    toSafeString(existing.name, "") ||
    toSafeString(existing.displayName, "") ||
    fallback
  );
}

function resolveLocation({ existing = {}, input = {} }) {
  if (input.location && typeof input.location === "object") {
    const location = {
      province: toSafeString(input.location.province, ""),
      district: toSafeString(input.location.district, ""),
      sector: toSafeString(input.location.sector, ""),
      cell: toSafeString(input.location.cell, ""),
    };
    const hasAnyLocationPart = Object.values(location).some((value) => value.length > 0);
    return hasAnyLocationPart ? location : null;
  }

  if (existing.location && typeof existing.location === "object") {
    return {
      province: toSafeString(existing.location.province, ""),
      district: toSafeString(existing.location.district, ""),
      sector: toSafeString(existing.location.sector, ""),
      cell: toSafeString(existing.location.cell, ""),
    };
  }

  return null;
}

export function buildCanonicalUserDocument({ firebaseUser, existingData = {}, profileData = {}, markProfileComplete = false, now = new Date() }) {
  const uid = toSafeString(firebaseUser?.uid || existingData.uid, "");
  const email = toSafeString(firebaseUser?.email || existingData.email, "");
  const username = resolveUsername({ existing: existingData, input: profileData, firebaseUser });
  const displayName = resolveName({ existing: existingData, input: profileData, fallback: username });
  const name = resolveName({ existing: existingData, input: profileData, fallback: displayName || username });
  const photoURL =
    toSafeString(profileData.photoURL, "") ||
    toSafeString(profileData.avatar, "") ||
    toSafeString(existingData.photoURL, "") ||
    toSafeString(existingData.avatar, "") ||
    toSafeString(firebaseUser?.photoURL, "");
  const profilePhone = toSafeString(profileData.phone, "") || toSafeString(existingData.phone, "");
  const authPhone = toSafeString(firebaseUser?.phoneNumber, "");
  const phoneNumber =
    authPhone ||
    toSafeString(profileData.phoneNumber, "") ||
    toSafeString(existingData.phoneNumber, "") ||
    profilePhone;
  const location = resolveLocation({ existing: existingData, input: profileData });

  return {
    uid,
    email,
    username,
    usernameLower: username.toLowerCase(),
    displayName,
    name,
    photoURL,
    avatar: photoURL,
    phone: profilePhone || null,
    phoneNumber: phoneNumber || null,
    phoneVerified:
      toSafeBoolean(profileData.phoneVerified, false) ||
      toSafeBoolean(existingData.phoneVerified, false) ||
      Boolean(authPhone),
    emailVerified: Boolean(firebaseUser?.emailVerified ?? existingData.emailVerified),
    location,
    hasCompletedProfile: markProfileComplete ? true : toSafeBoolean(existingData.hasCompletedProfile, false),
    role: toSafeString(existingData.role, "user"),
    profilePublic: toSafeBoolean(existingData.profilePublic, true),
    isActive: existingData.isActive !== false,
    points: toSafeNonNegativeInt(existingData.points, 0),
    ratingPoints: toSafeNonNegativeInt(existingData.ratingPoints, 0),
    followersCount: toSafeNonNegativeInt(existingData.followersCount, 0),
    followingCount: toSafeNonNegativeInt(existingData.followingCount, 0),
    schemaVersion: USER_SCHEMA_VERSION,
    createdAt: existingData.createdAt || now,
    updatedAt: now,
  };
}
